"use client";
import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LanguageIcon from "@mui/icons-material/Language";
import { useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "./LanguageProvider";

export const Navbarr = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentLang, changeLanguage, isInitialized } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isRTL = currentLang === "ar";

  // Check if we're on the home page
  const isHomePage = location.pathname === "/";

  // Translations
  const translations = {
    en: {
      navItems: [
        { name: "Home", id: "home", link: "/", isPage: false },
        { name: "About Us", id: "about", link: "/about", isPage: true },
        { name: "Services", id: "services", link: "/services", isPage: true },
        { name: "Blogs", id: "blogs", link: "/blogs", isPage: true },
        { name: "Contact", id: "contact", link: "/contact", isPage: true },
      ],
      btnConsultation: "Book Consultation",
    },
    ar: {
      navItems: [
        { name: "الرئيسية", id: "home", link: "/", isPage: false },
        { name: "من نحن", id: "about", link: "/about", isPage: true },
        { name: "خدماتنا", id: "services", link: "/services", isPage: true },
        { name: "المدونات", id: "blogs", link: "/blogs", isPage: true },
        { name: "اتصل بنا", id: "contact", link: "/contact", isPage: true },
      ],
      btnConsultation: "احجز استشارة",
    },
  };

  const t = translations[currentLang];
  const navItems = t.navItems;

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleNavigation = (item) => {
    // If it's a page (About, Services, Blogs, Contact)
    if (item.isPage) {
      navigate(item.link);
    } 
    // If it's Home (section on home page)
    else {
      // If we're not on home page, navigate to home first
      if (!isHomePage) {
        navigate("/");
        // Wait for navigation to complete then scroll
        setTimeout(() => {
          scrollToSection("home");
        }, 100);
      } else {
        // Already on home page, just scroll
        scrollToSection("home");
      }
    }
    setMobileOpen(false);
  };

  // Handle Book Consultation button click
  const handleBookConsultation = () => {
    // If we're not on home page, navigate to home first
    if (!isHomePage) {
      navigate("/");
      // Wait for navigation to complete then scroll to contact section
      setTimeout(() => {
        scrollToSection("contact");
      }, 100);
    } else {
      // Already on home page, just scroll to contact section
      scrollToSection("contact");
    }
    setMobileOpen(false);
  };

  const handleLanguageChange = (lang) => {
    changeLanguage(lang);
    setAnchorEl(null);
  };

  // Mobile Drawer
  const drawer = (
    <Box sx={{ width: 280, height: "100%", bgcolor: "#ffffff", color: "#333" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", p: 2 }}>
        <IconButton onClick={() => setMobileOpen(false)} sx={{ color: "#333" }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <List>
        {navItems.map((item) => (
          <ListItem
            key={item.name}
            onClick={() => handleNavigation(item)}
            sx={{ cursor: "pointer", "&:hover": { bgcolor: "rgba(201,160,61,0.1)" } }}
          >
            <ListItemText primary={item.name} />
          </ListItem>
        ))}
        <ListItem>
          <Button
            fullWidth
            sx={{
              bgcolor: "#c9a03d",
              color: "white",
              borderRadius: "30px",
              "&:hover": { bgcolor: "#a07d2e" },
            }}
            onClick={handleBookConsultation}
          >
            {t.btnConsultation}
          </Button>
        </ListItem>
        <Divider sx={{ my: 2 }} />
        <ListItem>
          <Box sx={{ display: "flex", gap: 1, width: "100%", justifyContent: "center" }}>
            <Button
              variant={currentLang === "en" ? "contained" : "outlined"}
              size="small"
              onClick={() => handleLanguageChange("en")}
              sx={{
                bgcolor: currentLang === "en" ? "#c9a03d" : "transparent",
                color: currentLang === "en" ? "white" : "#c9a03d",
                borderColor: "#c9a03d",
                "&:hover": { bgcolor: "#c9a03d", color: "white" },
              }}
            >
              EN
            </Button>
            <Button
              variant={currentLang === "ar" ? "contained" : "outlined"}
              size="small"
              onClick={() => handleLanguageChange("ar")}
              sx={{
                bgcolor: currentLang === "ar" ? "#c9a03d" : "transparent",
                color: currentLang === "ar" ? "white" : "#c9a03d",
                borderColor: "#c9a03d",
                "&:hover": { bgcolor: "#c9a03d", color: "white" },
              }}
            >
              عربي
            </Button>
          </Box>
        </ListItem>
      </List>
    </Box>
  );

  if (!isInitialized) {
    return null;
  }

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          bgcolor: "rgba(255,255,255,0.98)",
          boxShadow: "0 2px 15px rgba(0,0,0,0.05)",
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid rgba(0,0,0,0.05)",
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ justifyContent: "space-between", py: { xs: 1, sm: 1.5 } }}>
            <Box
              sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
              onClick={() => {
                if (!isHomePage) {
                  navigate("/");
                  setTimeout(() => {
                    scrollToSection("home");
                  }, 100);
                } else {
                  scrollToSection("home");
                }
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  color: "#1a1a2e",
                  letterSpacing: 1,
                  fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                }}
              >
                RUKN ALMUROOJ
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#c9a03d",
                  [isRTL ? "mr" : "ml"]: 1,
                  display: { xs: "none", sm: "block" },
                }}
              >
                {currentLang === "ar" ? "خدمات تصفية المعاملات" : "Documents Clearing Services"}
              </Typography>
            </Box>

            {!isMobile ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {navItems.map((item) => (
                  <Button
                    key={item.name}
                    onClick={() => handleNavigation(item)}
                    sx={{
                      color: "#333",
                      fontWeight: 500,
                      fontSize: "0.9rem",
                      px: 2,
                      py: 1,
                      "&:hover": {
                        color: "#c9a03d",
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    {item.name}
                  </Button>
                ))}
                <Button
                  onClick={handleBookConsultation}
                  sx={{
                    bgcolor: "#c9a03d",
                    color: "white",
                    borderRadius: "30px",
                    px: 3,
                    py: 1,
                    ml: 2,
                    "&:hover": { bgcolor: "#a07d2e" },
                  }}
                >
                  {t.btnConsultation}
                </Button>
                <IconButton
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  sx={{ color: "#c9a03d", ml: 1 }}
                >
                  <LanguageIcon />
                </IconButton>
              </Box>
            ) : (
              <IconButton onClick={() => setMobileOpen(true)} sx={{ color: "#c9a03d" }}>
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Language Popup Menu for Desktop */}
      <Drawer
        anchor="top"
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        sx={{ "& .MuiDrawer-paper": { width: "auto", mx: "auto", mt: 8, borderRadius: 2 } }}
      >
        <Box sx={{ display: "flex", p: 2, gap: 1 }}>
          <Button
            variant={currentLang === "en" ? "contained" : "outlined"}
            onClick={() => handleLanguageChange("en")}
            sx={{
              bgcolor: currentLang === "en" ? "#c9a03d" : "transparent",
              color: currentLang === "en" ? "white" : "#c9a03d",
              borderColor: "#c9a03d",
            }}
          >
            English
          </Button>
          <Button
            variant={currentLang === "ar" ? "contained" : "outlined"}
            onClick={() => handleLanguageChange("ar")}
            sx={{
              bgcolor: currentLang === "ar" ? "#c9a03d" : "transparent",
              color: currentLang === "ar" ? "white" : "#c9a03d",
              borderColor: "#c9a03d",
            }}
          >
            العربية
          </Button>
        </Box>
      </Drawer>

      {/* Mobile Drawer */}
      <Drawer anchor="right" open={mobileOpen} onClose={() => setMobileOpen(false)}>
        {drawer}
      </Drawer>
    </>
  );
};

export default Navbarr;